/**
 * @typedef {{ rowIndex: number, fieldKey: string }} SkipFieldCell
 */

/**
 * @param {SkipFieldCell[]} cells
 * @returns {Map<number, Set<string>>}
 */
export const toSkippedFieldsByRow = (cells) => {
    /** @type {Map<number, Set<string>>} */
    const map = new Map();
    for (const cell of cells ?? []) {
        const rowIndex = Number(cell?.rowIndex);
        const fieldKey = cell?.fieldKey;
        if (Number.isNaN(rowIndex) || !fieldKey) {
            continue;
        }
        if (!map.has(rowIndex)) {
            map.set(rowIndex, new Set());
        }
        map.get(rowIndex).add(fieldKey);
    }
    return map;
};

/**
 * @param {SkipFieldCell[]} cells
 * @param {number} rowIndex
 * @param {string} fieldKey
 */
export const isFieldSkippedForRow = (cells, rowIndex, fieldKey) =>
    (cells ?? []).some((cell) => cell.rowIndex === rowIndex && cell.fieldKey === fieldKey);

/**
 * @param {SkipFieldCell[]} cells
 * @param {number} rowIndex
 * @param {string} fieldKey
 * @param {boolean} skip
 * @returns {SkipFieldCell[]}
 */
export const toggleSkipFieldCell = (cells, rowIndex, fieldKey, skip) => {
    const list = [...(cells ?? [])];
    const existingIndex = list.findIndex(
        (cell) => cell.rowIndex === rowIndex && cell.fieldKey === fieldKey,
    );

    if (skip && existingIndex === -1) {
        list.push({rowIndex, fieldKey});
    } else if (!skip && existingIndex >= 0) {
        list.splice(existingIndex, 1);
    }

    return list;
};

/**
 * @param {import('./section-factories').PreventativeField} field
 */
export const resolveFieldSkipTarget = (field) => field?.skipTarget ?? field?.key;

/**
 * @param {import('./section-factories').PreventativeField} field
 * @param {Object} [issue]
 */
export const isPreventativeFieldSkippable = (field, issue) => {
    if (!field || field.skippable === false) {
        return false;
    }
    if (typeof field.skippable === 'function') {
        return !!field.skippable(issue);
    }
    return field.skippable === true;
};

/**
 * @param {number} rowIndex
 * @param {SkipFieldCell[]} cells
 * @param {Record<string, string>} labelByFieldKey
 * @returns {string[]}
 */
export const getSkippedFieldLabelsForRow = (rowIndex, cells, labelByFieldKey) =>
    (cells ?? [])
        .filter((cell) => cell.rowIndex === rowIndex)
        .map((cell) => labelByFieldKey[cell.fieldKey] ?? cell.fieldKey);

/**
 * @param {Array<{rowIndex: number, fieldKey: string}>} cells
 * @param {number} rowIndex
 * @returns {Set<string>}
 */
export const getSkippedFieldKeySetForRow = (cells, rowIndex) =>
    new Set(
        (cells ?? [])
            .filter((cell) => cell.rowIndex === rowIndex)
            .map((cell) => cell.fieldKey),
    );
